import { Grid, Tags as TagsModule, Utils } from 'common';
import isArray from 'lodash/isArray';
import isDate from 'lodash/isDate';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import React, { Component } from 'react';
import EmptyProductsGrid from './EmptyProductsGrid.component';

const { TagCellRenderer } = TagsModule;
const { GridPanel } = Grid;
const { Session } = Utils;

class ProductsGrid extends Component {
	constructor(props) {
		super(props);

		// WEBAPPS-27297, get the preserved column changes from `ProductsGridAgGrid` in `statefulViews`
		const { statefulViews } = Session.getPreferences();

		this.statefulViews =
			statefulViews && statefulViews !== ''
				? JSON.parse(decodeURIComponent(statefulViews))
				: {};

		this.state = {
			columnDefs: [
				{
					colId: 'SKU',
					headerName: 'SKU',
					field: 'SKU',
					minWidth: 155,
					headerCheckboxSelection: true,
					checkboxSelection: true,
				},
				{
					colId: 'Name',
					headerName: 'Name',
					field: 'Name',
					flex: 1,
					minWidth: 255,
				},
				{
					colId: 'CategoryId',
					headerName: 'Category',
					minWidth: 155,
					valueGetter: ({ data: { CategoryId } }) => {
						const { categoryOptions } = this.props;
						const category = categoryOptions.find(
							({ CategoryID }) => CategoryID === CategoryId
						);

						return category ? category.Name : '';
					},
				},
				{
					colId: 'Weight',
					headerName: 'Weight',
					field: 'Weight',
					minWidth: 135,
					cellRenderer({ value }) {
						return value > 0
							? `${Math.floor(value / 16)}lbs. ${value % 16}oz.`
							: '';
					},
				},
				{
					colId: 'CreateDate',
					headerName: 'Created',
					field: 'CreateDate',
					minWidth: 125,
					cellRenderer({ value }) {
						return isDate(value)
							? new Intl.DateTimeFormat('en-US', {
									year: 'numeric',
									month: '2-digit',
									day: '2-digit',
								}).format(value)
							: '';
					},
				},
				{
					colId: 'ModifyDate',
					headerName: 'Modified',
					field: 'ModifyDate',
					minWidth: 145,
					cellRenderer({ value }) {
						return isDate(value)
							? new Intl.DateTimeFormat('en-US', {
									year: 'numeric',
									month: '2-digit',
									day: '2-digit',
								}).format(value)
							: '';
					},
				},
				{
					colId: 'Tags',
					headerName: 'Tags',
					minWidth: 100,
					cellRenderer: 'TagCellRenderer',
					valueGetter: ({ data: { Tags = [] } }) => {
						const { tagOptions } = this.props;

						return Tags.map(id =>
							tagOptions.find(({ OrderTagID }) => OrderTagID === id)
						);
					},
					sortable: false,
				},
				{
					colId: 'UPC',
					headerName: 'UPC',
					field: 'UPC',
					minWidth: 100,
					sortable: false,
				},
			],
			frameworkComponents: {
				TagCellRenderer,
			},
		};
	}

	fetchData = nextPage => {
		const { onCurrentPageChange } = this.props;

		if (isFunction(onCurrentPageChange)) {
			onCurrentPageChange(nextPage);
		}
	};

	/**
	 * Preserve the column changes in users' preference
	 * @param {string} columnState
	 */
	onColumnStateChanged = columnState => {
		this.statefulViews = {
			...this.statefulViews,
			ProductsGridAgGrid: columnState,
		};

		Session.savePreferences([
			{
				Name: 'statefulViews',
				Value: encodeURIComponent(JSON.stringify(this.statefulViews)),
			},
		]);
	};

	onSelectionChanged = selections => {
		const { onSelectionChangedCallback } = this.props;
		const selectedProducts = selections.map(({ data }) => data);

		if (isFunction(onSelectionChangedCallback)) {
			onSelectionChangedCallback(selectedProducts);
		}
	};

	onSortChanged = sortModel => {
		const { onSortChangedCallback } = this.props;
		const sortColumn = isArray(sortModel) && sortModel[0] ? sortModel[0] : {};
		const { colId, sort: sortDir } = sortColumn;

		if (isFunction(onSortChangedCallback)) {
			onSortChangedCallback(colId, sortDir);
		}
	};

	render() {
		const {
			activeMenuItemId,
			pageSize,
			products,
			currentPage,
			totalCount,
			showPagination,
			onClickAddProductCallback,
			onGridReady,
		} = this.props;
		const { columnDefs, frameworkComponents } = this.state;
		const totalPages = Math.ceil(totalCount / pageSize);

		// WEBAPPS-27297, get the preserved column changes from `ProductsGridAgGrid`
		const columnState = isEmpty(this.statefulViews.ProductsGridAgGrid)
			? []
			: this.statefulViews.ProductsGridAgGrid;

		return (
			<div className="products-grid">
				<GridPanel
					rowData={products}
					columnDefs={columnDefs}
					columnState={columnState}
					currentPage={currentPage}
					pageSize={pageSize}
					showPagination={showPagination}
					totalPages={totalPages || 1}
					frameworkComponents={frameworkComponents}
					loadingOverlayParams={{
						loadingMessage: 'Loading Products...',
					}}
					noRowsOverlayParams={{
						EmptyGridView: EmptyProductsGrid,
						onClickAddProductCallback,
						activeMenuItemId,
					}}
					onColumnStateChanged={this.onColumnStateChanged}
					onGridReady={onGridReady}
					onSelectionChanged={this.onSelectionChanged}
					onSortChanged={this.onSortChanged}
					fetchData={this.fetchData}
				/>
			</div>
		);
	}
}

export default ProductsGrid;
